/**
 * Hook for Telegram Mini App functionality
 */

import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  init,
  miniApp,
  themeParams,
  mainButton,
  backButton,
  viewport,
  hapticFeedback,
  closingBehavior,
  openTelegramLink,
  popup,
} from '@telegram-apps/sdk-react';

// SDK UI features state (auth is handled by TelegramAuthProvider)
interface TelegramAppState {
  isReady: boolean;
  isDarkMode: boolean;
  themeParams: typeof themeParams | null;
}

export function useTelegramApp() {
  const [state, setState] = useState<TelegramAppState>({
    isReady: false,
    isDarkMode: false,
    themeParams: null,
  });

  // Track current handlers to properly remove them before adding new ones
  const mainButtonHandlerRef = useRef<(() => void) | null>(null);
  const backButtonHandlerRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const initApp = async () => {
      try {
        // Initialize SDK for UI features (auth is already set by TelegramAuthProvider)
        init();

        // Mount components
        if (miniApp.mount.isAvailable()) {
          miniApp.mount();
        }
        if (themeParams.mount.isAvailable()) {
          themeParams.mount();
        }
        if (viewport.mount.isAvailable()) {
          viewport.mount();
          viewport.expand();
        }
        if (mainButton.mount.isAvailable()) {
          mainButton.mount();
        }
        if (backButton.mount.isAvailable()) {
          backButton.mount();
        }
        if (closingBehavior.mount.isAvailable()) {
          closingBehavior.mount();
        }

        // Bind CSS variables for theme changes
        if (themeParams.bindCssVars.isAvailable()) {
          themeParams.bindCssVars();
        }

        setState({
          isReady: true,
          isDarkMode: miniApp.isDark(),
          themeParams: themeParams,
        });

        // Signal ready to Telegram
        if (miniApp.ready.isAvailable()) {
          miniApp.ready();
        }
      } catch (error) {
        console.error('Failed to initialize Telegram Mini App:', error);
        // Still set as ready for development outside Telegram
        setState((prev) => ({ ...prev, isReady: true }));
      }
    };

    initApp();
  }, []);

  // Show main button
  const showMainButton = useCallback((text: string, onClick: () => void) => {
    if (mainButton.setParams.isAvailable()) {
      // Remove previous handler before adding new one to prevent accumulation
      if (mainButtonHandlerRef.current && mainButton.offClick.isAvailable()) {
        mainButton.offClick(mainButtonHandlerRef.current);
      }

      mainButton.setParams({
        text,
        isVisible: true,
        isEnabled: true,
      });
      mainButton.onClick(onClick);
      mainButtonHandlerRef.current = onClick;
    }
  }, []);

  // Show main button in disabled (greyed out) state
  const showMainButtonDisabled = useCallback((text: string) => {
    if (mainButton.setParams.isAvailable()) {
      // Remove handler when showing disabled to prevent stale handlers
      if (mainButtonHandlerRef.current && mainButton.offClick.isAvailable()) {
        mainButton.offClick(mainButtonHandlerRef.current);
        mainButtonHandlerRef.current = null;
      }
      mainButton.setParams({
        text,
        isVisible: true,
        isEnabled: false,
      });
    }
  }, []);

  // Hide main button
  const hideMainButton = useCallback(() => {
    if (mainButton.setParams.isAvailable()) {
      // Remove handler when hiding to prevent stale handlers
      if (mainButtonHandlerRef.current && mainButton.offClick.isAvailable()) {
        mainButton.offClick(mainButtonHandlerRef.current);
        mainButtonHandlerRef.current = null;
      }
      mainButton.setParams({ isVisible: false });
    }
  }, []);

  // Set main button loading state
  const setMainButtonLoading = useCallback((isLoading: boolean) => {
    if (mainButton.setParams.isAvailable()) {
      mainButton.setParams({
        isLoaderVisible: isLoading,
        isEnabled: !isLoading,
      });
    }
  }, []);

  // Show back button
  const showBackButton = useCallback((onClick: () => void) => {
    if (backButton.show.isAvailable()) {
      // Remove previous handler before adding new one to prevent accumulation
      if (backButtonHandlerRef.current && backButton.offClick.isAvailable()) {
        backButton.offClick(backButtonHandlerRef.current);
      }

      backButton.show();
      backButton.onClick(onClick);
      backButtonHandlerRef.current = onClick;
    }
  }, []);

  // Hide back button
  const hideBackButton = useCallback(() => {
    if (backButton.hide.isAvailable()) {
      // Remove handler when hiding to prevent stale handlers
      if (backButtonHandlerRef.current && backButton.offClick.isAvailable()) {
        backButton.offClick(backButtonHandlerRef.current);
        backButtonHandlerRef.current = null;
      }
      backButton.hide();
    }
  }, []);

  // Haptic feedback
  const haptic = useMemo(() => ({
    impact: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' = 'medium') => {
      if (hapticFeedback.impactOccurred.isAvailable()) {
        hapticFeedback.impactOccurred(style);
      }
    },
    notification: (type: 'error' | 'success' | 'warning') => {
      if (hapticFeedback.notificationOccurred.isAvailable()) {
        hapticFeedback.notificationOccurred(type);
      }
    },
    selection: () => {
      if (hapticFeedback.selectionChanged.isAvailable()) {
        hapticFeedback.selectionChanged();
      }
    },
  }), []);

  // Enable closing confirmation (use when form has data)
  const enableClosingConfirmation = useCallback(() => {
    if (closingBehavior.enableConfirmation.isAvailable()) {
      closingBehavior.enableConfirmation();
    }
  }, []);

  // Disable closing confirmation
  const disableClosingConfirmation = useCallback(() => {
    if (closingBehavior.disableConfirmation.isAvailable()) {
      closingBehavior.disableConfirmation();
    }
  }, []);

  // Close app
  const closeApp = useCallback(() => {
    if (miniApp.close.isAvailable()) {
      miniApp.close();
    }
  }, []);

  // Open bot with chart deep link
  const openBotWithChart = useCallback((chartId: string) => {
    const botUsername = import.meta.env.VITE_BOT_USERNAME || 'natalon_bot';
    const deepLink = `https://t.me/${botUsername}?start=view_${chartId}`;

    if (openTelegramLink.isAvailable()) {
      openTelegramLink(deepLink);

      // Close miniapp explicitly on mobile for better UX
      // On Desktop, don't close - it causes race conditions
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
      if (isMobile && miniApp.close.isAvailable()) {
        miniApp.close();
      }
    }
  }, []);

  // Show confirmation popup (native Telegram popup)
  const showConfirmPopup = useCallback(async (options: {
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
  }): Promise<boolean> => {
    if (popup.open.isAvailable()) {
      const buttonId = await popup.open({
        title: options.title,
        message: options.message,
        buttons: [
          { id: 'cancel', type: 'default', text: options.cancelText || 'Отмена' },
          { id: 'confirm', type: 'destructive', text: options.confirmText || 'Удалить' },
        ],
      });
      return buttonId === 'confirm';
    }
    // Fallback for development outside Telegram
    return window.confirm(`${options.title}\n${options.message}`);
  }, []);

  return {
    ...state,
    showMainButton,
    showMainButtonDisabled,
    hideMainButton,
    setMainButtonLoading,
    showBackButton,
    hideBackButton,
    haptic,
    enableClosingConfirmation,
    disableClosingConfirmation,
    closeApp,
    openBotWithChart,
    showConfirmPopup,
  };
}

export default useTelegramApp;
